import React, { useEffect, useState } from "react";
import { api, API, getToken } from "@/lib/api";
import { PageHeader, Card, Spinner } from "@/components/UIKit";
import { fmtNOK, fmtKwh, fmtNumber, fmtDateTime } from "@/lib/format";
import { Download, FileSpreadsheet, FileText, TrendingUp, Users, Zap, Receipt, Calculator, BookOpen, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function Rapporter() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [acctStatus, setAcctStatus] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [acctLog, setAcctLog] = useState([]);

  const loadStatus = async () => {
    const [s, l] = await Promise.all([api.get("/accounting/status"), api.get("/accounting/log")]);
    setAcctStatus(s.data); setAcctLog(l.data);
  };

  useEffect(() => {
    setData(null);
    api.get(`/reports/summary?days=${days}`).then(({ data }) => setData(data));
  }, [days]);

  useEffect(() => { loadStatus(); }, []);

  const download = async (path, filename) => {
    const resp = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const sync = async (system) => {
    setSyncing(system);
    try {
      const { data } = await api.post(`/accounting/sync/${system}`);
      toast.success(`${system}: ${data.exported_invoices} fakturaer + ${data.exported_payments} betalinger sendt`);
      if (data.errors?.length) toast.error(`${data.errors.length} feil — se logg`);
      loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync feilet"); }
    finally { setSyncing(null); }
  };

  return (
    <div data-testid="rapporter-page">
      <PageHeader
        title="Rapporter og eksport"
        subtitle="Salg, omsetning, forbruk og regnskapseksport"
        actions={
          <div className="flex items-center gap-1 bg-zinc-900/60 rounded-md p-1 border border-zinc-900">
            {[30, 90, 180, 365].map((d) => (
              <button key={d} onClick={() => setDays(d)}
                className={`px-3 py-1 rounded text-xs transition-colors ${days === d ? "bg-zinc-800 text-zinc-100" : "text-zinc-500 hover:text-zinc-200"}`}
                data-testid={`period-${d}`}>{d}d</button>
            ))}
          </div>
        }
      />

      {!data ? <Spinner /> : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <RC icon={Users} label="Nye avtaler" value={fmtNumber(data.new_contracts)} sub={`Netto: ${data.growth >= 0 ? "+" : ""}${data.growth}`} accent="text-emerald-400" />
          <RC icon={Receipt} label="Fakturaer" value={fmtNumber(data.invoices_count)} />
          <RC icon={Zap} label="Forbruk" value={fmtKwh(data.consumption_kwh)} accent="text-amber-400" />
          <RC icon={TrendingUp} label="Omsetning" value={fmtNOK(data.revenue_nok)} sub={`${data.collection_rate}% innkrevd`} accent="text-emerald-400" />
        </div>
      )}

      {/* Regnskapseksport */}
      <div className="mb-6">
        <div className="font-heading text-lg mb-3">Regnskapseksport</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {["tripletex", "fiken"].map((sys) => {
            const s = acctStatus?.[sys];
            const Icon = sys === "tripletex" ? Calculator : BookOpen;
            const label = sys === "tripletex" ? "Tripletex" : "Fiken";
            return (
              <Card key={sys} className="p-5" testid={`acct-${sys}`}>
                <div className="flex items-start gap-3">
                  <div className={`h-10 w-10 rounded-md grid place-items-center shrink-0 border ${s?.enabled ? "bg-emerald-500/10 border-emerald-500/30" : "bg-zinc-900 border-zinc-800"}`}>
                    <Icon className={`h-5 w-5 ${s?.enabled ? "text-emerald-400" : "text-zinc-500"}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-heading font-medium text-zinc-100">{label}</div>
                      {s?.enabled && s?.sandbox && <span className="text-[10px] uppercase bg-amber-500/10 text-amber-400 border border-amber-500/20 px-1.5 py-0.5 rounded">Sandbox</span>}
                      {s?.enabled && !s?.sandbox && <span className="text-[10px] uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-1.5 py-0.5 rounded">Produksjon</span>}
                      {!s?.enabled && <span className="text-[10px] uppercase bg-zinc-800 text-zinc-500 px-1.5 py-0.5 rounded">Ikke aktivert</span>}
                    </div>
                    {s?.enabled ? (
                      <div className="text-xs text-zinc-400 mt-2">
                        <span className="text-amber-400">{s.pending_invoices}</span> fakturaer venter · <span className="text-amber-400">{s.pending_payments}</span> betalinger venter
                        {s.last_sync_at && <><br /><span className="text-zinc-500">Sist synket: {fmtDateTime(s.last_sync_at)}</span></>}
                      </div>
                    ) : (
                      <div className="text-xs text-zinc-500 mt-2">
                        Aktiver under <span className="text-emerald-400">Innstillinger → Integrasjoner</span> først.
                      </div>
                    )}
                    <button onClick={() => sync(sys)} disabled={!s?.enabled || syncing === sys}
                      className="mt-3 h-8 px-3 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black text-xs font-medium flex items-center gap-1.5"
                      data-testid={`sync-${sys}`}>
                      {syncing === sys ? <Loader2 className="h-3 w-3 animate-spin" /> : <Calculator className="h-3 w-3" />}
                      Kjør synkronisering nå
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {acctLog.length > 0 && (
          <Card className="mt-3 overflow-hidden">
            <div className="p-3 border-b border-zinc-900 text-xs uppercase tracking-wider text-zinc-500">Siste eksport</div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase text-zinc-500 bg-zinc-950/60">
                  <th className="py-1.5 px-3 font-medium">Tid</th>
                  <th className="py-1.5 px-3 font-medium">System</th>
                  <th className="py-1.5 px-3 font-medium">Type</th>
                  <th className="py-1.5 px-3 font-medium">Ekstern ID</th>
                  <th className="py-1.5 px-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {acctLog.slice(0, 15).map((l) => (
                  <tr key={l.id} className="border-t border-zinc-900">
                    <td className="py-1 px-3 text-zinc-500">{fmtDateTime(l.ts)}</td>
                    <td className="py-1 px-3 text-zinc-300">{l.system}</td>
                    <td className="py-1 px-3 text-zinc-400">{l.resource_type}</td>
                    <td className="py-1 px-3 font-mono text-emerald-400">{l.external_id || "—"}</td>
                    <td className="py-1 px-3">
                      {l.status === "success" ? <span className="text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> OK</span>
                        : <span className="text-red-400 inline-flex items-center gap-1"><XCircle className="h-3 w-3" /> {l.error_message?.slice(0, 40)}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <button onClick={() => download("/reports/export/customers.csv", "kunder.csv")}
          className="text-left p-5 rounded-lg border border-zinc-900 hover:border-emerald-500/30 bg-[#0c0c0c] transition-colors group" data-testid="dl-customers">
          <FileSpreadsheet className="h-6 w-6 text-emerald-400 mb-3" />
          <div className="font-heading font-medium text-zinc-100 mb-1">Kunder (CSV)</div>
          <div className="text-xs text-zinc-500">Eksporter alle kunder til Excel/CSV</div>
          <div className="mt-3 text-xs text-emerald-400 flex items-center gap-1 group-hover:gap-2 transition-all"><Download className="h-3 w-3" /> Last ned</div>
        </button>
        <button onClick={() => download("/reports/export/invoices.csv", "fakturaer.csv")}
          className="text-left p-5 rounded-lg border border-zinc-900 hover:border-emerald-500/30 bg-[#0c0c0c] transition-colors group" data-testid="dl-invoices">
          <FileSpreadsheet className="h-6 w-6 text-emerald-400 mb-3" />
          <div className="font-heading font-medium text-zinc-100 mb-1">Fakturaer (CSV)</div>
          <div className="text-xs text-zinc-500">Alle fakturaer med beløp, MVA og status</div>
          <div className="mt-3 text-xs text-emerald-400 flex items-center gap-1 group-hover:gap-2 transition-all"><Download className="h-3 w-3" /> Last ned</div>
        </button>
        <div className="text-left p-5 rounded-lg border border-zinc-900 bg-[#0c0c0c] opacity-60">
          <FileText className="h-6 w-6 text-zinc-500 mb-3" />
          <div className="font-heading font-medium text-zinc-100 mb-1">Regnskapsrapport (PDF)</div>
          <div className="text-xs text-zinc-500">Bruk Tripletex/Fiken-eksport ovenfor for regnskap</div>
        </div>
      </div>
    </div>
  );
}

function RC({ icon: Icon, label, value, sub, accent }) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">{label}</div>
        <Icon className={`h-4 w-4 ${accent || "text-zinc-500"}`} />
      </div>
      <div className="font-heading text-3xl font-medium tracking-tight tabular-nums">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </Card>
  );
}
